<template>
  <FinancialAdvisorProBillingPlanCardSkeleton v-if="isLoading" />
  <section v-else class="has-border-light has-border-radius p-5">
    <div class="is-size-1 has-text-weight-semibold">
      <div class="is-flex is-justify-content-space-between is-align-items-center">
        <div>
          {{ plan.name }}
          <span
            v-if="!!plan.savePercentage"
            class="has-text-success m-l-5"
            :data-e2e="FinancialAdvisorEstateCypressAttribute.plan.views.SAVE_PERCENTAGE"
          >
            {{ $t('financialadvisor.save-percentage', { percentage: plan.savePercentage }) }}
          </span>
        </div>
        <TagView
          v-if="isCurrentPlan"
          type="is-primary is-light"
          size="is-small"
          class="has-text-weight-normal"
        >
          {{ $t('financialadvisor.your-current-plan') }}
        </TagView>
      </div>
      <p class="m-b-5">
        {{ plan.formattedPrice }}
        <span class="has-text-secondary-grey">{{ plan.priceSubText }}</span>
      </p>
    </div>
    <p class="m-b-10">
      {{ $t('financialadvisor.billing-plan-tax-calculation') }}
    </p>
    <ButtonView
      type="is-primary"
      class="m-b-10 p-4 is-full-width"
      :loading="isLoadingButton"
      @click="onGetPlan"
      :data-e2e="FinancialAdvisorEstateCypressAttribute.modal.buttons.GET_PLAN"
    >
      <span class="has-text-weight-semibold">{{ buttonText }}</span>
    </ButtonView>
    <p v-dompurify-html="plan.description" class="m-b-10" />
    <p v-dompurify-html="plan.note" class="m-b-10" />
    <section class="m-b-10">
      <p class="has-text-weight-semibold m-b-5">{{ $t('financialadvisor.in-this-plan') }}</p>
      <BillingCardInclusionsExclusions
        :inclusions="plan.inclusions"
        :show-subtitles="false"
        inclusion-icon="check-circle-1-filled"
        class="is-full-width"
        :is-horizontal="false"
      >
        <template #inclusion-text="{ inclusion }">
          <span v-dompurify-html="inclusion" />
        </template>
      </BillingCardInclusionsExclusions>
    </section>
  </section>
</template>

<script>
import { CheckoutSessionCart } from '@/modules/billing/models/CheckoutSessionCart';
import { FinancialAdvisorEstateCypressAttribute } from '@/modules/financialadvisor/constants/FinancialAdvisorEstateCypressAttribute';
import { FinancialAdvisorProBillingPlanForm } from '@/modules/financialadvisor/models/FinancialAdvisorProBillingPlanForm';
import { CheckoutRoute } from '@/modules/billing/constants/CheckoutRoute';
import { PlatformProduct } from '@/modules/platformproduct/models/PlatformProduct';
import ButtonView from '@/modules/core/ui/components/wrappers/ButtonView.vue';
import BillingCardInclusionsExclusions from '@/modules/estate/components/presentational/BillingCardInclusionsExclusions.vue';
import FinancialAdvisorProBillingPlanCardSkeleton from '@/modules/financialadvisor/components/skeletons/FinancialAdvisorProBillingPlanCardSkeleton.vue';
import FinancialAdvisorCheckoutService from '@/modules/financialadvisor/services/FinancialAdvisorCheckoutService';
import DocumentUIService from '@/modules/core/app/services/DocumentUIService';
import ModalService from '@/modules/core/ui/services/ModalService';
import TagView from '@/modules/core/ui/components/wrappers/TagView.vue';
import BillingAdvisorProChangePlanUIService from '@/modules/billing/services/BillingAdvisorProChangePlanUIService';
import UserAddAddressModal from '@/modules/user/components/stateful/UserAddAddressModal.vue';
import { HasAuthUserMixin } from '@/modules/core/app/mixins/HasAuthUserMixin';

export default {
  compatConfig: {
    COMPONENT_V_MODEL: true
  },
  name: 'FinancialAdvisorProBillingPlanCard',
  mixins: [HasAuthUserMixin],
  components: {
    ButtonView,
    BillingCardInclusionsExclusions,
    FinancialAdvisorProBillingPlanCardSkeleton,
    TagView
  },
  props: {
    plan: {
      type: FinancialAdvisorProBillingPlanForm,
      required: true
    },
    platformProduct: {
      type: PlatformProduct
    }
  },
  static: {
    FinancialAdvisorEstateCypressAttribute
  },
  data() {
    return {
      isLoading: true,
      isLoadingButton: false
    };
  },
  computed: {
    isCurrentPlan() {
      return this.platformProduct?.productKey === this.plan.productKey;
    },
    buttonText() {
      return this.isCurrentPlan
        ? this.$t('financialadvisor.continue-with-this-plan')
        : this.$t('financialadvisor.get-this-plan');
    }
  },
  methods: {
    showAddressModal() {
      ModalService.show({
        parent: this,
        component: UserAddAddressModal,
        props: {
          value: this.authUser.clone()
        },
        events: {
          'address-updated': this.createCheckoutSession
        }
      });
    },
    async onGetPlan() {
      if (this.isCurrentPlan) {
        ModalService.closeAll();
        return;
      }

      if (!this.platformProduct) {
        // As we need jurisdictionId for invoice taxes later
        if (!!this.authUser.contact.address?.jurisdictionId) {
          this.createCheckoutSession();
          return;
        }

        this.showAddressModal();
        return;
      }

      BillingAdvisorProChangePlanUIService.showUpgradeDowngradeModal(
        {
          parent: this
        },
        this.plan,
        this.platformProduct
      );
    },
    async createCheckoutSession() {
      this.isLoadingButton = true;
      const successUrl = DocumentUIService.constructUrl(
        CheckoutRoute.CHECKOUT_SUCCESS.path,
        {
          session_id: '{CHECKOUT_SESSION_ID}'
        },
        false
      );

      try {
        const response = await FinancialAdvisorCheckoutService.createSession(
          new CheckoutSessionCart({
            successUrl,
            productKeys: [this.plan.productKey]
          })
        );

        DocumentUIService.openUrl(response.url, '_self');
      } finally {
        this.isLoadingButton = false;
      }
    },
    async loadPlanPrice() {
      try {
        await this.plan.loadPlanPrice();
      } finally {
        this.isLoading = false;
      }
    }
  },
  mounted() {
    this.loadPlanPrice();
  }
};
</script>
